/**
 * app/shared/src/config/pricing.generated.ts
 *
 * AUTO-GENERATED from backend/functions/src/config/pricing.json.
 * DO NOT EDIT — edit the JSON source and re-run `node scripts/render-pricing.js --generate-react`.
 *
 * Generated: 2026-11-04T04:09:26.602Z
 *
 * This is the ONE place React apps should pull pricing from.
 * Keystone Phase 2.5 — Pricing SOT build-time codegen.
 */

interface AgentPricing {
  monthlyPrice: number;
  /** Internal / dashboard name — "Google Agent". Also feeds Ava's own words. */
  displayName: string;
  /** What the BUYER sees on the Stripe checkout page, receipt and billing portal. */
  invoiceName: string;
  invoiceDescription: string;
  stripePriceIdSecretName: string;
  voiceMinutesIncluded?: number;
  voiceOverageRate?: number;
  voiceCostPerMinuteInternal?: number;
  // Every field an agent can carry had to be enumerated here, so ADDING A FIELD
  // TO pricing.json BROKE THE FRONTEND BUILD — the generated literal contained a
  // property the generated interface did not declare (TS2353). It broke exactly
  // that way on 2026-07-19 when the overage build added
  // voiceOverageMeterEventName / voiceOveragePriceIdSecretName: the backend
  // suite was green (15,196 tests) because the backend reads pricing.json
  // directly, and the ripple landed in the SECOND copy under app/shared.
  //
  // PricingConfig below already solved this one level up with an index
  // signature, for the same reason and with the same symptom. AgentPricing now
  // does too, so a new agent field flows through instead of red-lighting CI.
  // Named fields above are kept for the ones code actually reads — they still
  // get real types; the signature only stops unknown extras from being an error.
  [extra: string]: unknown;
}

interface PricingConfig {
  currency: string;
  base: {
    monthlyPrice: number;
    displayName: string;
    invoiceName: string;
    invoiceDescription: string;
    defaultPromoDiscountPercent: number;
  };
  agents: Record<string, AgentPricing>;
  multiLocation: { googleAgent: { pricePerLocationMonthly: number; note: string; [extra: string]: unknown } };
  promoTiers: Array<{ affiliateBaseDiscountPercent: number; affiliateRevenueSharePerMonth: number; note: string }>;
  referral: { referrerCreditPerActiveReferralMonthly: number; referredFirstMonthDiscountPercent: number; note: string };
  annual: { monthsCharged: number; renewalNoticeDays: number[]; note: string };
  platformFee: { stripePlatformFeePercent: number; note: string };
  // Nested index signatures allow _*Note companion-fields (e.g.,
  // _googleWorkspaceEmailMonthlyNote, _domainsByTldFallbackNote) that
  // pricing.json uses for inline annotation. Without these, the generated
  // pricing.generated.ts fails tsc with TS2353 on any literal that includes
  // an underscore-prefixed metadata field. Added 2026-05-18 (S5 Control
  // Center deploy hit this — pre-existing Keystone drift).
  passThrough: { googleWorkspaceEmailMonthly: number; note: string; [extra: string]: unknown };
  [extra: string]: unknown;  // allow $schema, _description, _lastUpdated metadata
}

export const pricing: PricingConfig = {
  "currency": "USD",
  "base": {
    "monthlyPrice": 75,
    "displayName": "Ava",
    "defaultPromoDiscountPercent": 0,
    "invoiceName": "Ava (your assistant)",
    "invoiceDescription": "Ava answers your texts, keeps track of every customer, and runs the skills you switch on."
  },
  "agents": {
    "google": {
      "monthlyPrice": 75,
      "displayName": "Google Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_GOOGLE",
      "invoiceName": "Get you leads from Google",
      "invoiceDescription": "Ava keeps your Google Business Profile fresh, posts updates and offers, and asks your customers for reviews. Covers one business location; additional locations are billed separately."
    },
    "phone": {
      "monthlyPrice": 75,
      "displayName": "Phone Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_PHONE",
      "voiceMinutesIncluded": 200,
      "voiceOverageRate": 0.17,
      "voiceCostPerMinuteInternal": 0.1,
      "voiceOverageMeterEventName": "phone_voice_minutes",
      "voiceOveragePriceIdSecretName": "STRIPE_PRICE_ID_PHONE_OVERAGE",
      "invoiceName": "Answer your phone calls",
      "invoiceDescription": "Ava answers every call day or night, answers your customers' questions, and books appointments straight into your calendar. Includes 200 talk minutes a month; extra minutes are $0.17 each."
    },
    "social": {
      "monthlyPrice": 75,
      "displayName": "Social Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_SOCIAL",
      "invoiceName": "Run your social media",
      "invoiceDescription": "Ava keeps your Facebook and Instagram active, posts before-and-afters and promos, and responds to comments."
    },
    "web": {
      "monthlyPrice": 75,
      "displayName": "Web Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_WEB",
      "invoiceName": "Manage your website",
      "invoiceDescription": "Ava builds or takes over your website, chats with visitors, and books them 24/7 right on the site."
    },
    "ads": {
      "monthlyPrice": 75,
      "displayName": "Ads Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_ADS",
      "invoiceName": "Manage your paid ads",
      "invoiceDescription": "Ava creates your ads, watches performance daily, and tunes campaigns to lower your cost per customer. Your ad budget is paid to Google or Facebook directly."
    },
    "video": {
      "monthlyPrice": 75,
      "displayName": "Video Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_VIDEO",
      "invoiceName": "Make videos for your business",
      "invoiceDescription": "Ava makes marketing videos from your photos and clips, posts them to your channels, and gets you found on YouTube."
    },
    "nurture": {
      "monthlyPrice": 75,
      "displayName": "Nurture Skill",
      "stripePriceIdSecretName": "STRIPE_PRICE_ID_NURTURE",
      "invoiceName": "Follow up with every lead and customer",
      "invoiceDescription": "Ava texts new leads back in seconds, remembers every interaction, and wins back the customers who've gone quiet."
    }
  },
  "multiLocation": {
    "googleAgent": {
      "pricePerLocationMonthly": 75,
      "note": "Only the Google Agent charges per location. All other agents are shared across locations at single-instance pricing."
    }
  },
  "annual": {
    "monthsCharged": 10,
    "renewalNoticeDays": [
      35,
      20
    ],
    "note": "Pay yearly, 2 months free (John, 2026-08-16). The ANNUAL PRICE IS DERIVED — monthly x monthsCharged — and must NEVER be typed anywhere: not in checkout copy, not in an Ava script, not in a Stripe price we hand-made in the dashboard. monthsCharged=10 means a 16.7% discount, squarely inside the normal SaaS band, off a product carrying 89-97% gross margin (still ~75-80% after). WHY IT MATTERS MORE THAN A PRICING TWEAK: platform fixed costs are ~$489/mo, so ONE annual everything-tier client pays ~$6,000 up front - over a year of fixed costs from a single customer. It is the cheapest capital available: no dilution, no interest. renewalNoticeDays: RESEARCHED 2026-08-16, changed from [30,7] to [35,20]. THE B2B EXEMPTION IS GONE - the usual 'auto-renewal laws are consumer-only, we sell B2B' reasoning stopped being reliable in 2026: Colorado (C.R.S. 6-1-732 as amended by SB25-145, contracts on/after 2026-02-16) struck 'personal, family, or household purposes' and now reaches business buyers at 25-40 days, and Virginia's 'consumer' expressly includes small business at 30-60 days. Our customers - plumbers, roofers, HVAC, CPAs - are exactly who those cover. WHY NOT 30: it sits simultaneously on a FLOOR (several states 'no less than 30') and a CEILING (Massachusetts 940 CMR 38.05 caps notice at 30), so one timezone or cron slip breaks compliance in one direction or the other. WHY NOT 7: it satisfies nothing 30 did not, and misses NY GOL 5-903 (15-30). 35 clears every floor; 20 catches MA's ceiling and NY GOL. No state prohibits extra notices. 14 days - the original instinct - would have violated Colorado, Virginia, Illinois, Florida, DC, Utah and Georgia. These laws govern NOTICE and stopping the NEXT charge; they do NOT require refunding a paid term. Sorted descending by the consumer; each fires once per term. Research is sourced but NOT legal advice - four points await counsel (see todo annual-renewal-notice-lawyer-confirm). Spec: strategy/annual-billing-arc.md section 4."
  },
  "promoTiers": [
    {
      "affiliateBaseDiscountPercent": 100,
      "affiliateRevenueSharePerMonth": 0,
      "note": "Pure member benefit — no revenue share to affiliate"
    },
    {
      "affiliateBaseDiscountPercent": 75,
      "affiliateRevenueSharePerMonth": 18.75,
      "note": "Light revenue share"
    },
    {
      "affiliateBaseDiscountPercent": 50,
      "affiliateRevenueSharePerMonth": 37.5,
      "note": "Balanced split"
    },
    {
      "affiliateBaseDiscountPercent": 25,
      "affiliateRevenueSharePerMonth": 56.25,
      "note": "Maximum affiliate revenue"
    }
  ],
  "referral": {
    "referrerCreditPerActiveReferralMonthly": 37.5,
    "referredFirstMonthDiscountPercent": 100,
    "note": "Referrer earns $37.50/mo credit per active referred client, ongoing (changed from $75 on 2026-08-09). Referred client gets 100% off base fee for the first month only. Credits stack. WHY $37.50 not $75: the credit must stay BELOW the marginal profit of the cheapest possible customer (a 1-skill, base-waived client pays $75/mo, ~$20/mo to serve, so ~$55 marginal profit). At $75 a single referral of a low-bill client made the referrer FREE and a referral chain went cash-negative; $37.50 sits safely under the $55 line, so no referral graph can go negative. 'Referrals to free' is now per-client = ceil(actual post-promo bill / 37.5), NEVER a fixed number — see referralsToFree() in pricing.ts. Rationale + math: memory/project_referral_program.md."
  },
  "platformFee": {
    "stripePlatformFeePercent": 0,
    "note": "We do not take a platform fee on client invoicing via Stripe Connect. Client's customer pays standard Stripe processing (2.9% + $0.30). Set to non-zero later if we enable platform revenue on invoicing."
  },
  "passThrough": {
    "googleWorkspaceEmailMonthly": 7.2,
    "domainsByTldFallback": {
      "com": 10.44,
      "net": 11.62,
      "org": 10.44,
      "co": 25.49,
      "io": 32.88,
      "ai": 63.96,
      "dev": 14.39,
      "app": 14.39,
      "biz": 16.45,
      "info": 14.99,
      "us": 8.39
    },
    "note": "External prices we pass through to clients as separate line items. Per strategy report Webmaster section: zero markup — actual cost is the charge. Domain registration billed annually; Google Workspace billed monthly per user. Initial purchase (domain or first Workspace user) charges immediately one-time to avoid eating cost if client cannot pay later. Failed payment → 30-day grace → domain expires at Cloudflare / Workspace user suspended."
  }
};

export type AgentKey = 'google' | 'phone' | 'social' | 'web' | 'ads' | 'video' | 'nurture';

// ── Derived values (matches backend/functions/src/config/pricing.ts) ────────

const fmt = (n: number) => Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;

export function basePriceMonthly(): number { return pricing.base.monthlyPrice; }
export function agentPriceMonthly(agent: AgentKey): number { return pricing.agents[agent].monthlyPrice; }

export function totalMonthly(hiredAgents: AgentKey[]): number {
  return hiredAgents.reduce((sum, k) => sum + agentPriceMonthly(k), pricing.base.monthlyPrice);
}

export function fullStackMonthly(): number {
  return totalMonthly(Object.keys(pricing.agents) as AgentKey[]);
}

export function formatMonthly(amount: number): string { return `${fmt(amount)}/mo`; }
export function formatMonthlyLong(amount: number): string { return `${fmt(amount)}/month`; }
export function formatUSD(amount: number): string { return fmt(amount); }

// ── Annual billing — DERIVED, never typed (mirrors the backend helpers) ──────
// The checkout toggle reads these. A literal "10 months" or "\$6,000" anywhere in
// React is a second source of truth for money; call these instead.

export type BillingInterval = 'month' | 'year';

export function annualMonthsCharged(): number { return pricing.annual.monthsCharged; }
export function annualMonthsFree(): number { return Math.max(0, 12 - annualMonthsCharged()); }

export function annualPrice(monthlyAmount: number): number {
  if (!Number.isFinite(monthlyAmount) || monthlyAmount <= 0) return 0;
  return monthlyAmount * annualMonthsCharged();
}

export function annualTotal(hiredAgents: AgentKey[]): number { return annualPrice(totalMonthly(hiredAgents)); }

export function annualSavings(monthlyAmount: number): number {
  if (!Number.isFinite(monthlyAmount) || monthlyAmount <= 0) return 0;
  return monthlyAmount * annualMonthsFree();
}

export function annualDiscountPercent(): number { return (annualMonthsFree() / 12) * 100; }

/** Recognised monthly revenue for an annual plan — NOT the monthly sticker price. */
export function annualMonthlyEquivalent(monthlyAmount: number): number { return annualPrice(monthlyAmount) / 12; }

export function priceForInterval(monthlyAmount: number, interval: BillingInterval): number {
  return interval === 'year' ? annualPrice(monthlyAmount) : monthlyAmount;
}

export function formatAnnual(amount: number): string { return `${fmt(amount)}/year`; }

export function formatForInterval(amount: number, interval: BillingInterval): string {
  return interval === 'year' ? formatAnnual(amount) : formatMonthly(amount);
}

// ── String constants for direct use in JSX/TSX ───────────────────────────────

export const basePriceNum = "75";
export const agentPriceNum = "75";
export const fullStackPriceNum = "600";
export const fullStackWaivedPriceNum = "525";
export const referralCreditNum = "37.5";
export const annualMonthsChargedNum = "10";
export const annualMonthsFreeNum = "2";
export const annualDiscountPercentNum = "17";
export const basePriceAmount = "$75";
export const agentPriceAmount = "$75";
export const fullStackPriceAmount = "$600";
export const fullStackWaivedPriceAmount = "$525";
export const basePrice = "$75/mo";
export const agentPrice = "$75/mo";
export const fullStackPrice = "$600/mo";
export const avaEverythingPriceNum = "600";
export const avaEverythingPrice = "$600/mo";
export const avaEverythingPriceLong = "$600/month";
export const avaEverythingPerDay = "$20.00/day";
export const avaEverythingPerDayShort = "$20/day";
export const fullStackWaivedPrice = "$525/mo";
export const referralCredit = "$37.50/mo";
export const starterPriceNum = "150";
export const starterPriceAmount = "$150";
export const starterPrice = "$150/mo";
export const starterPerDay = "$5.00/day";
export const starterPerDayAmount = "$5.00";
export const fullStackPerDay = "$20.00/day";
export const fullStackPerDayAmount = "$20.00";
export const agentPerDay = "$2.50/day";
export const agentPerDayAmount = "$2.50";
export const basePerDay = "$2.50/day";
export const basePerDayAmount = "$2.50";
export const basePerDayNum = "2.5";
export const agentPerDayNum = "2.5";
export const invoiceNamesJson = "{\"base\":\"Ava (your assistant)\",\"google\":\"Get you leads from Google\",\"phone\":\"Answer your phone calls\",\"social\":\"Run your social media\",\"web\":\"Manage your website\",\"ads\":\"Manage your paid ads\",\"video\":\"Make videos for your business\",\"nurture\":\"Follow up with every lead and customer\"}";
export const basePriceBilled = "billed at $75/month";
export const agentPriceBilled = "billed at $75/month";
export const starterPriceBilled = "billed at $150/month";
export const fullStackPriceBilled = "billed at $600/month";
export const basePriceLong = "$75/month";
export const agentPriceLong = "$75/month";
export const fullStackPriceLong = "$600/month";
export const phoneVoiceMinutesIncluded = "200";
export const phoneVoiceOverageRate = "$0.17";
export const workspaceEmailPrice = "$7.20/mo";
export const workspaceEmailAmount = "$7.20";
export const affiliateRevShare100 = "$0/mo";
export const affiliateRevShare100Amount = "$0";
export const affiliateRevShare75 = "$18.75/mo";
export const affiliateRevShare75Amount = "$18.75";
export const affiliateRevShare50 = "$37.50/mo";
export const affiliateRevShare50Amount = "$37.50";
export const affiliateRevShare25 = "$56.25/mo";
export const affiliateRevShare25Amount = "$56.25";
export const googleAgentPrice = "$75/mo";
export const phoneAgentPrice = "$75/mo";
export const socialAgentPrice = "$75/mo";
export const webAgentPrice = "$75/mo";
export const adsAgentPrice = "$75/mo";
export const videoAgentPrice = "$75/mo";
export const nurtureAgentPrice = "$75/mo";
export const locationAddonPrice = "$75/mo";
